import { useSaveShell } from "../../api/queries";
import type { Shell } from "../../api/types";

const CHOICES: { value: Shell; label: string }[] = [
  { value: "zsh", label: "zsh" },
  { value: "bash", label: "bash" },
];

interface ShellSectionProps {
  /** The shell currently on file. */
  current: Shell;
}

/**
 * Which shell a workspace drops you into.
 *
 * A preference rather than a fleet-wide setting: zsh is the default because
 * the research team asked for it, and somebody who lives in bash should not
 * have to run `chsh` every morning — the container is rebuilt from its image
 * at every start, so a change made inside a workspace does not survive it.
 * That is exactly why this lives on the account and not in a dotfile.
 *
 * Built with the same radio-inside-a-label pattern as the rest of the app
 * (`.choice` + `data-selected`), so it is keyboard-navigable as one group and
 * needs no styles of its own.
 *
 * Args:
 *     current: The shell on file.
 *
 * Returns:
 *     The two choices, and what changing one does.
 */
export function ShellSection({ current }: ShellSectionProps): JSX.Element {
  const save = useSaveShell();

  return (
    <div>
      <fieldset className="choice-row" disabled={save.isPending}>
        <legend className="sr-only">Shell</legend>
        {CHOICES.map((choice) => (
          <label
            key={choice.value}
            className="choice"
            data-selected={current === choice.value}
          >
            <input
              type="radio"
              name="shell"
              className="sr-only"
              value={choice.value}
              checked={current === choice.value}
              onChange={() => save.mutate({ shell: choice.value })}
            />
            <span className="mono">{choice.label}</span>
          </label>
        ))}
      </fieldset>

      {/* Said before it is asked: the running workspace keeps its shell, and
          somebody who flips this then reconnects would otherwise read it as
          the setting not working. */}
      <p className="aside__meta">
        Applies to the workspaces you open next. One already running keeps the
        shell it started with.
      </p>

      {save.isError ? (
        <p className="form__error" role="alert">
          {save.error.message}
        </p>
      ) : null}
    </div>
  );
}
